import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { TrendingUp, ExternalLink, ArrowLeft, Sparkles } from "lucide-react";
import GrowthChip from "../components/GrowthChip";
import { countryName } from "../lib/countries";
import CopilotChat from "./CopilotChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => {
  if (v == null) return "—";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${Math.round(v / 1_000)}k`;
  return `$${Math.round(v)}`;
};

const SORTS = [
  { key: "mrr", label: "Highest monthly revenue" },
  { key: "total", label: "Highest all-time revenue" },
  { key: "growth", label: "Fastest growing (30d)" },
  { key: "name", label: "Name (A–Z)" },
];

// Plain-English caption explaining what the current sort is doing.
const SORT_HELP = {
  mrr: "showing the biggest monthly earners first",
  total: "showing who's earned the most all-time first",
  growth: "showing the fastest-growing first",
  name: "listed A to Z",
};

// Honest "Should I build this?" verdict for a bootstrapped product — built ONLY
// from data we actually have (real revenue, 30-day trend, category competition,
// open markets). No fabricated pain/feature data.
const buildVerdict = (c, categorySize, openMarkets, similar, health) => {
  const growth = c.growth_mrr_30d;
  const growing = growth == null || growth >= 0;
  const hasOpen = openMarkets && openMarkets.length > 0;
  const crowded = categorySize > 150;
  const hardMoney = health && (health.difficulty === "Hard" || health.difficulty === "Brutal");
  const signals = [
    { ok: true, label: `Makes ${fmt(c.monthly_usd)}/mo` },
    { ok: growing, label: growing ? "Growing or stable" : "Revenue shrinking" },
    { ok: hasOpen, label: hasOpen ? `${openMarkets.length} open market${openMarkets.length > 1 ? "s" : ""}` : "No obvious open market" },
    { ok: !crowded, label: crowded ? `Crowded · ${categorySize} rivals` : `Few rivals · ${categorySize}` },
  ];
  if (health && health.tried)
    signals.push({ ok: !hardMoney, label: hardMoney ? `Hard to monetize · ${Math.round(health.monetization_rate * 100)}% reach $50+/mo` : `Monetizes OK · ${Math.round(health.monetization_rate * 100)}% reach $50+/mo` });
  const good = growing && hasOpen && c.monthly_usd >= 200 && !crowded && !hardMoney;
  const caution = !growing || crowded || hardMoney;
  return {
    label: good ? "Worth cloning" : caution ? "Proceed with caution" : "Worth a look",
    good: good && !caution,
    tagline: good
      ? "A proven money-maker with open markets still to enter."
      : !growing
        ? "It already makes money, but revenue is shrinking — only clone with a clear improvement."
        : hardMoney
          ? "It earns, but most products in this category never make money — monetization is the hard part."
          : crowded
            ? "Profitable, but the category is crowded — you'll need a sharp wedge."
            : "Profitable and clonable — validate demand before you build.",
    signals,
  };
};

const quickRead = (c, categorySize, openMarkets, similar, health) => {
  const g = c.growth_mrr_30d;
  const crowded = categorySize > 150;
  const out = [
    `💰 Proven: makes ${fmt(c.monthly_usd)} per month today${g != null ? ` (${g >= 0 ? "up" : "down"} ${Math.abs(Math.round(g))}% in the last 30 days)` : ""}.`,
  ];
  if (openMarkets && openMarkets.length)
    out.push(`🌍 Best new market: ${countryName(openMarkets[0])}${openMarkets.length > 1 ? ` (plus ${openMarkets.length - 1} more)` : ""} has no local version of this yet.`);
  out.push(`📊 Our data has ${categorySize} revenue-proven products in this product's category (${c.category || "this category"}) — ${crowded ? "it's crowded, so you'll need a sharp angle" : "competition looks manageable"}.`);
  if (health && health.tried)
    out.push(`⚖️ Reality check: of all ${health.tried} products ever tracked in ${c.category || "this category"}, only ${Math.round(health.monetization_rate * 100)}% reached $50+/mo and ${health.graveyard} died trying — ${health.difficulty === "Hard" || health.difficulty === "Brutal" ? "monetization is the real challenge here" : "monetization is fairly achievable here"}.`);
  if (similar && similar.length)
    out.push(`🔁 ${similar.length} similar products are also earning — the model clearly works.`);
  return out;
};

/** Evaluate → Bootstrapped: browse individual revenue-proven indie products. */
export default function BootstrappedBrowser() {
  const [facets, setFacets] = useState({ total: 0, categories: [], countries: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("mrr");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const PAGE_SIZE = 30;

  const fetchPage = useCallback((pageNum, replace) => {
    replace ? setLoading(true) : setLoadingMore(true);
    axios
      .get(`${API}/bootstrapped/companies`, { params: { q, category, sort, page: pageNum, page_size: PAGE_SIZE } })
      .then((r) => {
        setTotal(r.data.total || 0);
        setItems((prev) => (replace ? (r.data.items || []) : [...prev, ...(r.data.items || [])]));
        setPage(pageNum);
      })
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, [q, category, sort]);

  useEffect(() => {
    axios.get(`${API}/bootstrapped/facets`).then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  // Reset to page 1 whenever filters/sort change (debounced for search typing).
  useEffect(() => {
    const t = setTimeout(() => fetchPage(1, true), 250);
    return () => clearTimeout(t);
  }, [fetchPage]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    setDetail(null);
    axios.get(`${API}/bootstrapped/companies/${selected}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [selected]);

  const cats = useMemo(() => [{ value: "all", count: facets.total }, ...(facets.categories || [])], [facets]);

  if (selected) {
    return <Detail detail={detail} onBack={() => setSelected(null)} onPick={setSelected} />;
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="bootstrapped-browser">
      <div className="text-xs font-bold tracking-widest text-emerald-600 mb-2">EVALUATE · BOOTSTRAPPED WINNERS</div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-2">Real products. Real revenue.</h1>
      <p className="text-zinc-500 max-w-3xl">
        {facets.total.toLocaleString()} bootstrapped products with self-reported monthly revenue (via TrustMRR).
        Each one proves the model already makes money — find one to clone, then see which markets are still open.
      </p>

      <div className="flex flex-wrap items-center gap-3 mt-6 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search product or category…"
          className="flex-1 min-w-[220px] px-4 py-2.5 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:border-emerald-400"
          data-testid="bootstrapped-search"
        />
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-zinc-500 whitespace-nowrap hidden sm:inline">Sort by</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2.5 rounded-lg border border-zinc-200 text-sm" data-testid="bootstrapped-sort">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {cats.slice(0, 14).map((c) => (
          <button
            key={c.value}
            onClick={() => setCategory(c.value)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              category === c.value ? "bg-emerald-600 text-white border-emerald-600" : "border-zinc-200 text-zinc-600 hover:border-emerald-300"
            }`}
            data-testid={`bootstrapped-cat-${c.value}`}
          >
            {c.value === "all" ? "all" : c.value} {c.count ? <span className="opacity-60">{c.count}</span> : null}
          </button>
        ))}
      </div>

      <div className="text-sm text-zinc-500 mb-3" data-testid="bootstrapped-count">
        {total.toLocaleString()} products · <span className="text-zinc-400">{SORT_HELP[sort]}</span>
      </div>
      {loading ? (
        <div className="text-zinc-400 text-sm py-10">Loading…</div>
      ) : (
        <div className="space-y-2">
          {items.map((i) => (
            <button
              key={i.id}
              onClick={() => setSelected(i.id)}
              className="w-full text-left flex items-center justify-between gap-4 p-4 rounded-xl border border-zinc-200 bg-white hover:border-emerald-300 transition-colors"
              data-testid={`bootstrapped-row-${i.id}`}
            >
              <div className="min-w-0">
                <div className="font-semibold text-zinc-900 truncate">{i.name}</div>
                <div className="text-xs text-zinc-500 truncate">
                  {[i.category, countryName(i.country)].filter(Boolean).join(" · ")}{i.description ? ` — ${i.description.slice(0, 110)}` : ""}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-bold text-emerald-700">{fmt(i.monthly_usd)}<span className="text-[10px] text-emerald-500">/mo</span></div>
                {i.total_usd > 0 && (
                  <div className={`text-[10px] ${sort === "total" ? "text-zinc-700 font-semibold" : "text-zinc-400"}`}>
                    {fmt(i.total_usd)} all-time
                  </div>
                )}
                {i.growth_mrr_30d != null && <GrowthChip value={i.growth_mrr_30d} className="text-[10px]" />}
              </div>
            </button>
          ))}
        </div>
      )}

      {!loading && items.length < total && (
        <div className="flex justify-center mt-6">
          <button
            onClick={() => fetchPage(page + 1, false)}
            disabled={loadingMore}
            data-testid="bootstrapped-load-more"
            className="px-6 py-2.5 rounded-full border border-zinc-200 text-sm font-semibold text-zinc-700 hover:border-emerald-300 disabled:opacity-50 transition-colors"
          >
            {loadingMore ? "Loading…" : `Load more · ${(total - items.length).toLocaleString()} more`}
          </button>
        </div>
      )}
    </div>
  );
}

const Detail = ({ detail, onBack, onPick }) => {
  if (!detail) return <div className="max-w-4xl mx-auto p-6 text-zinc-400">Loading…</div>;
  if (detail.error) return <div className="max-w-4xl mx-auto p-6 text-zinc-500">Not found.</div>;
  const c = detail.company;
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="bootstrapped-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-4" data-testid="bootstrapped-back">
        <ArrowLeft size={15} /> Back
      </button>

      <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 mb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-zinc-900">{c.name}</h1>
            <div className="text-sm text-zinc-500 mt-1">{[c.category, countryName(c.country)].filter(Boolean).join(" · ")}</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-extrabold text-emerald-700">{fmt(c.monthly_usd)}<span className="text-xs text-emerald-500">/mo</span></div>
            <div className="text-[10px] uppercase tracking-wide text-zinc-400">monthly revenue</div>
            {c.growth_mrr_30d != null && <GrowthChip value={c.growth_mrr_30d} long className="text-xs mt-0.5" />}
          </div>
        </div>
        {c.description && <p className="text-sm text-zinc-700 mt-3">{c.description}</p>}
        <div className="flex flex-wrap gap-4 mt-4 text-xs text-zinc-500">
          <span>Total revenue <span className="text-zinc-400">(all-time)</span>: <b className="text-zinc-800">{fmt(c.total_usd)}</b></span>
          {c.website && <a href={c.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-emerald-700 hover:underline"><ExternalLink size={12} /> Website</a>}
          {c.profile_url && <a href={c.profile_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-emerald-700 hover:underline"><ExternalLink size={12} /> TrustMRR</a>}
        </div>
        <p className="text-[10px] text-zinc-400 mt-3">Self-reported revenue · via TrustMRR.</p>
      </div>

      {/* Should I build this? — verdict + plain-English read */}
      {(() => {
        const v = buildVerdict(c, detail.category_size || 0, detail.clone_angle?.open_markets || [], detail.similar || [], detail.category_health);
        const qr = quickRead(c, detail.category_size || 0, detail.clone_angle?.open_markets || [], detail.similar || [], detail.category_health);
        const tone = v.good
          ? { card: "bg-emerald-50 border-emerald-200", title: "text-emerald-700" }
          : { card: "bg-amber-50 border-amber-200", title: "text-amber-700" };
        return (
          <>
            <div className={`rounded-xl border ${tone.card} p-5 mb-5`} data-testid="bootstrapped-verdict">
              <div className="text-[11px] font-bold tracking-widest text-zinc-400 mb-1">SHOULD I CLONE THIS?</div>
              <div className={`text-2xl font-extrabold ${tone.title}`}>{v.good ? "✅ " : "⚠️ "}{v.label}</div>
              <p className="text-sm text-zinc-700 mt-1">{v.tagline}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {v.signals.map((s, i) => (
                  <span key={i} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${s.ok ? "border-emerald-300 text-emerald-700 bg-white" : "border-zinc-200 text-zinc-400 bg-white"}`} data-testid={`verdict-signal-${i}`}>
                    {s.ok ? "✓" : "✕"} {s.label}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-zinc-400 mt-3">A read of the revenue evidence below — not financial advice. Always verify before acting.</p>
            </div>

            <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-5">
              <div className="text-[11px] font-bold tracking-widest text-zinc-400 mb-3">QUICK READ — IN PLAIN ENGLISH</div>
              <ul className="space-y-2.5">
                {qr.map((line, i) => (
                  <li key={i} className="text-sm text-zinc-800 leading-snug" data-testid={`quick-read-${i}`}>{line}</li>
                ))}
              </ul>
            </div>
          </>
        );
      })()}

      {/* Clone-this-for-market angle */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-5">
        <div className="flex items-center gap-2 text-sm font-bold text-zinc-900 mb-2"><TrendingUp size={15} className="text-violet-600" /> Where you could clone it</div>
        {detail.clone_angle.open_markets.length ? (
          <>
            <p className="text-xs text-zinc-500 mb-3">
              Our data has <b>{detail.category_size} revenue-proven products</b> in this product's category, <b>{c.category}</b>. These markets have no local version listed yet — open lanes to clone into:
            </p>
            <div className="flex flex-wrap gap-2">
              {detail.clone_angle.open_markets.map((m) => (
                <span key={m} className="px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold" data-testid={`clone-market-${m}`}>{countryName(m)}</span>
              ))}
            </div>
          </>
        ) : (
          <p className="text-xs text-zinc-500">
            Our data has <b>{detail.category_size} revenue-proven products</b> in this product's category, <b>{c.category}</b>, and it's already sold across every major market. There's no obvious <b>geographic</b> gap here, so win on a sharper wedge, audience, or price instead of a new country.
          </p>
        )}
      </div>

      {/* Similar earners */}
      {detail.similar?.length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-5">
          <div className="text-sm font-bold text-zinc-900 mb-3">Similar products making money</div>
          <div className="space-y-2">
            {detail.similar.map((s) => (
              <button key={s.id} onClick={() => onPick(s.id)} className="w-full text-left flex items-center justify-between gap-3 p-2.5 rounded-lg border border-zinc-200 hover:border-emerald-300" data-testid={`similar-${s.id}`}>
                <div className="min-w-0"><div className="text-sm font-semibold text-zinc-900 truncate">{s.name}</div><div className="text-[11px] text-zinc-500 truncate">{[s.category, countryName(s.country)].filter(Boolean).join(" · ")}</div></div>
                <div className="text-sm font-bold text-emerald-700">{fmt(s.monthly_usd)}<span className="text-[10px] text-emerald-500">/mo</span></div>
              </button>
            ))}
          </div>
        </div>
      )}

      <AskAboutThis context={{ type: "bootstrapped", id: c.id, label: c.name }} label={`Ask the Advisor about cloning ${c.name}`} />
    </div>
  );
};

// Collapsible contextual co-pilot launcher — opens an embedded grounded chat
// pre-seeded with the focus entity so a layman can just ask in plain English.
const AskAboutThis = ({ context, label }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-5" data-testid="ask-about-this">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="ask-about-this-open"
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors"
        >
          <Sparkles size={17} /> {label}
        </button>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-bold tracking-widest text-violet-600">ADVISOR · GROUNDED IN REAL DATA</div>
            <button onClick={() => setOpen(false)} className="text-xs text-zinc-400 hover:text-zinc-700" data-testid="ask-about-this-close">Close</button>
          </div>
          <CopilotChat context={context} compact />
        </div>
      )}
    </div>
  );
};
